import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Volume2, ChevronLeft, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert snake_case / underscore keys to Title Case display labels
const toLabel = str =>
  str ? str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

// Locked pillar badge styles (Phase 1B taxonomy)
const PILLAR_STYLE = {
  healthcare: { background: 'var(--op-teal)',  color: '#fff' },
  government:  { background: 'var(--op-navy)', color: '#fff' },
  business:    { background: 'var(--op-gold)', color: '#fff' },
  education:   { background: '#5E3F8F',        color: '#fff' },
};

/* ─── Shared state components ─────────────────────────────────────────── */

const LoadingState = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="content-item-loading">
    <div className="text-center">
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
        style={{ borderColor: 'var(--op-teal)' }}
      />
      <p className="text-gray-500 text-sm">Loading article…</p>
    </div>
  </div>
);

const DraftGuard = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="content-item-draft">
    <div className="text-center max-w-sm mx-auto px-6 py-16">
      <AlertCircle size={36} className="mx-auto mb-4" style={{ color: 'var(--op-gold)' }} />
      <h1 className="text-2xl font-bold mb-2 text-gray-900">Not Available</h1>
      <p className="text-gray-500 text-sm mb-6">This article has not been published yet.</p>
      <Link to="/" className="text-sm font-medium underline" style={{ color: 'var(--op-teal)' }}
        data-testid="draft-home-link">
        Return to Homepage
      </Link>
    </div>
  </div>
);

const NotFoundState = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="content-item-not-found">
    <div className="text-center max-w-sm mx-auto px-6 py-16">
      <h1 className="text-5xl font-bold mb-3" style={{ color: 'var(--op-navy)' }}>404</h1>
      <p className="text-xl font-semibold mb-2 text-gray-800">Article Not Found</p>
      <p className="text-gray-500 text-sm mb-6">
        The article you're looking for doesn't exist or has been removed.
      </p>
      <Link to="/" className="text-sm font-medium underline" style={{ color: 'var(--op-teal)' }}
        data-testid="notfound-home-link">
        Return to Homepage
      </Link>
    </div>
  </div>
);

/* ─── SEO helpers ─────────────────────────────────────────────────────── */

function applySEO(item) {
  document.title = `${item.seo_title || item.title} — Opportunity Post`;
  const desc = item.seo_description || item.summary || item.dek || '';
  const ogImg = item.og_image_url || item.hero_image_url || item.featured_image_url || '';
  setMeta('name', 'description', desc);
  setMeta('property', 'og:title',       item.seo_title || item.title);
  setMeta('property', 'og:description', desc);
  setMeta('property', 'og:image',       ogImg);
  setMeta('property', 'og:type',        'article');
  if (item.canonical_url) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
    link.href = item.canonical_url;
  }
}

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.content = content;
}

/* ─── Main page component ─────────────────────────────────────────────── */

export const ContentItemPage = () => {
  const { slug } = useParams();
  const [item,     setItem]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isDraft,  setIsDraft]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setIsDraft(false);
    setItem(null);

    axios.get(`${API}/content/slug/${slug}`)
      .then(res => {
        if (cancelled) return;
        const data = res.data;
        if (data.status !== 'published') {
          setIsDraft(true);
        } else {
          setItem(data);
          applySEO(data);
        }
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [slug]);

  if (loading)           return <LoadingState />;
  if (isDraft)           return <DraftGuard />;
  if (notFound || !item) return <NotFoundState />;

  /* ── Computed display values ── */
  const heroImage   = item.hero_image_url || item.featured_image_url || null;
  const pillarStyle = PILLAR_STYLE[item.primary_pillar] || { background: 'var(--op-navy)', color: '#fff' };

  const publishedDate = item.published_at
    ? format(new Date(item.published_at), 'MMMM d, yyyy')
    : null;

  const readTime = item.estimated_read_minutes
    ? `${item.estimated_read_minutes} min read`
    : null;

  const showAudioPlayer = Boolean(item.audio_enabled && item.audio_url);

  const audioDuration = item.audio_duration_seconds
    ? `${Math.floor(item.audio_duration_seconds / 60)}:${String(item.audio_duration_seconds % 60).padStart(2, '0')}`
    : null;

  const daysLeft = (item.show_countdown && item.expires_at)
    ? Math.max(0, Math.ceil((new Date(item.expires_at) - Date.now()) / 86400000))
    : null;
  const countdownVisible = daysLeft !== null && daysLeft <= (item.countdown_start_days || 7);

  const hasTags   = item.tags && item.tags.length > 0;
  const hasTopics = item.editorial_topics && item.editorial_topics.length > 0;

  return (
    <article className="min-h-screen bg-white" data-testid="content-item-page">

      {/* ── Hero image ──────────────────────────────────────────────── */}
      {heroImage && (
        <figure className="w-full m-0" data-testid="article-hero">
          <div className="w-full overflow-hidden bg-gray-100" style={{ maxHeight: '520px' }}>
            <img
              src={heroImage}
              alt={item.image_caption || item.title}
              className="w-full object-cover"
              style={{ maxHeight: '520px', objectPosition: 'center 30%' }}
            />
          </div>
          {(item.image_caption || item.image_credit) && (
            <figcaption
              className="text-xs text-gray-400 text-right px-4 py-1.5 bg-gray-50"
              data-testid="article-image-credit"
            >
              {item.image_caption}
              {item.image_caption && item.image_credit ? ' — ' : ''}
              {item.image_credit}
            </figcaption>
          )}
        </figure>
      )}

      {/* ── Article body ────────────────────────────────────────────── */}
      <div className="container-custom py-10">

        {/* Taxonomy chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="article-chips">
          {item.primary_pillar && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
              style={pillarStyle}
              data-testid="article-pillar-chip"
            >
              {toLabel(item.primary_pillar)}
            </span>
          )}
          {item.content_format && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-gray-100 text-gray-600"
              data-testid="article-format-chip"
            >
              {toLabel(item.content_format)}
            </span>
          )}
          {item.website_section && item.website_section !== item.primary_pillar && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
              data-testid="article-section-chip"
            >
              {toLabel(item.website_section)}
            </span>
          )}
          {countdownVisible && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700"
              data-testid="article-countdown-chip"
            >
              {daysLeft === 0 ? 'Expires today' : `${daysLeft}d left`}
            </span>
          )}
        </div>

        {/* Title */}
        <h1
          className="text-4xl sm:text-5xl font-bold leading-tight mb-4"
          style={{ color: 'var(--op-navy)' }}
          data-testid="article-title"
        >
          {item.title}
        </h1>

        {/* Dek */}
        {item.dek && (
          <p className="text-xl text-gray-600 leading-relaxed mb-6" data-testid="article-dek">
            {item.dek}
          </p>
        )}

        {/* Meta bar: byline · date · read time · audio badge */}
        <div
          className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-gray-500 border-y border-gray-200 py-3.5 mb-8"
          data-testid="article-meta-bar"
        >
          {item.byline && (
            <span className="font-semibold text-gray-800" data-testid="article-byline">
              By {item.byline}
            </span>
          )}
          {publishedDate && (
            <span data-testid="article-date">{publishedDate}</span>
          )}
          {readTime && (
            <span className="flex items-center gap-1.5" data-testid="article-read-time">
              <Clock size={13} />
              {readTime}
            </span>
          )}
          {showAudioPlayer && (
            <span
              className="flex items-center gap-1.5 font-medium"
              style={{ color: 'var(--op-teal)' }}
              data-testid="article-audio-badge"
            >
              <Volume2 size={13} />
              Audio
            </span>
          )}
        </div>

        {/* Sponsor disclosure */}
        {item.is_sponsored && (
          <div
            className="rounded-lg px-4 py-3 mb-8 text-sm border"
            style={{ background: '#FFF8EC', borderColor: 'var(--op-gold)', color: '#7A5200' }}
            data-testid="article-sponsor-disclosure"
          >
            <span className="font-bold">Sponsored Content</span>
            {item.sponsor_name && <span> · {item.sponsor_name}</span>}
            {item.sponsor_disclosure && (
              <p className="mt-1 text-xs opacity-80">{item.sponsor_disclosure}</p>
            )}
          </div>
        )}

        {/* Audio player */}
        {showAudioPlayer && (
          <div
            className="rounded-xl p-5 mb-8 border"
            style={{ background: '#F0FAFA', borderColor: 'var(--op-teal-soft, #52BDB4)' }}
            data-testid="article-audio-player"
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--op-teal)' }}
              >
                <Volume2 size={16} className="text-white" />
              </div>
              <div>
                <p className="font-semibold text-sm text-gray-800">Listen to this article</p>
                {audioDuration && (
                  <p className="text-xs text-gray-500">{audioDuration} min</p>
                )}
              </div>
            </div>
            <audio
              controls
              src={item.audio_url}
              className="w-full"
              data-testid="audio-element"
            >
              Your browser does not support audio playback.
            </audio>
          </div>
        )}

        {/* Body HTML */}
        <div
          className="prose prose-lg prose-headings:text-gray-900 prose-a:text-teal-700 prose-a:no-underline hover:prose-a:underline max-w-none"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.body || '') }}
          data-testid="article-body"
        />

        {/* Editorial topics */}
        {hasTopics && (
          <div className="mt-10 pt-8 border-t border-gray-100" data-testid="article-topics">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Topics</p>
            <div className="flex flex-wrap gap-2">
              {item.editorial_topics.map((topic, i) => (
                <span
                  key={i}
                  className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-600"
                  data-testid={`topic-chip-${i}`}
                >
                  {toLabel(topic)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {hasTags && (
          <div className="mt-6" data-testid="article-tags">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Tags</p>
            <div className="flex flex-wrap gap-2">
              {item.tags.map((tag, i) => (
                <span
                  key={i}
                  className="px-3 py-1 rounded-full text-sm border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                  data-testid={`tag-chip-${i}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Back navigation */}
        <div className="mt-12 pt-6 border-t border-gray-100">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-700 transition-colors"
            data-testid="back-to-home"
          >
            <ChevronLeft size={15} />
            Back to Homepage
          </Link>
        </div>

      </div>
    </article>
  );
};
